import type {ReactElement} from 'react';
import {useEffect} from 'react';
import React, {useState} from 'react';
import styles from '@Panels/share-panel/share-panel.module.scss';
import type {ShareDesignAccessOptsProps} from '@Panels/share-panel/components/share-design-access-opts/share-design-access-opts.types';
import {ShareDesignAccessLoadingKeys} from '@Panels/share-panel/components/share-design-access-opts/share-design-access-opts.types';
import {Text, TextSize} from '@Components/text';
import {DesignPermissionLevel} from '@Panels/share-panel/share-panel.types';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Dropdown} from '@Components/dropdown-v2';
import {getDesignPermissionLevelText, getRemoveContributorErrorMessage} from '@Panels/share-panel/share-panel-library';
import {DropdownDefaultSelectBox} from '@Components/dropdown-v2/components/dropdown-default-select-box';
import {ControlledListItemType3} from '@Components/controlled-list/components/controlled-list-item-type-3';
import {ControlledListItemSize} from '@Components/controlled-list/controlled-list.types';
import Divider from '@Components/divider';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {ColorTypes, ControlledListItemType7} from '@/components/controlled-list/components/controlled-list-item-type-7';
import {InviteStateType} from '@Panels/share-panel/components/invite-state/invite-state.types';
import {useModalData} from '@Components/modal/hooks/useModalData';
import {MODALS_IDS} from '@Components/modal-container';
import {useAppDispatch} from '@/hooks';
import {updatePanelPropsForModal} from '@Components/modal-container/modal-container-reducer';
import {closePanel} from '@Components/panel/panel-reducer';
import {openErrorModal} from '@Modals/error-modal';
import {getUserId} from '@Libraries/user.library';
import {Button, Size, Type} from '@Components/button';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

export function ShareDesignAccessOpts({
  posterHashedId,
  designerContributorInfo,
  onPermissionLevelUpdate,
  onRemoveUser,
  onResendInvite,
  isMobile = false,
  idDesignOwner,
}: ShareDesignAccessOptsProps): ReactElement<any> {
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isResendCooldownActive, setIsResendCooldownActive] = useState(false);
  const modalData = useModalData(MODALS_IDS.MORE_OPTIONS_MODAL);
  const dispatch = useAppDispatch();
  const currentUserId = getUserId();

  const getInitialLoadingStates = (): Record<ShareDesignAccessLoadingKeys, boolean> => ({
    [ShareDesignAccessLoadingKeys.EDIT]: false,
    [ShareDesignAccessLoadingKeys.VIEW]: false,
    [ShareDesignAccessLoadingKeys.REMOVE]: false,
  });
  const [loadingStates, setLoadingStates] = useState<Record<ShareDesignAccessLoadingKeys, boolean>>(getInitialLoadingStates());

  useEffect(() => {
    if (!modalData) {
      return;
    }

    dispatch(
      updatePanelPropsForModal({
        modalId: MODALS_IDS.MORE_OPTIONS_MODAL,
        panelProps: {
          ...modalData.panelProps,
          content: getPopup(),
        },
      })
    );
  }, [designerContributorInfo, loadingStates, isDropdownOpen]);

  const setItemLoading = (key: string, value: boolean): void => {
    if (!isMobile) return;
    setLoadingStates((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const onClickPermissionLevel = (updatedPermissionLevel: DesignPermissionLevel): void => {
    const key = updatedPermissionLevel === DesignPermissionLevel.CAN_EDIT ? ShareDesignAccessLoadingKeys.EDIT : ShareDesignAccessLoadingKeys.VIEW;
    setItemLoading(key, true);
    setIsLoading(true);

    closeModal();

    void window.PMW.writeLocal('poster/updateUserContributorInvitationPermission', {
      posterHashedId: posterHashedId,
      idUser: designerContributorInfo?.idUser,
      permissionLevel: updatedPermissionLevel,
    })
      .then(() => {
        setItemLoading(key, false);
        setIsLoading(false);
        onPermissionLevelUpdate(updatedPermissionLevel, designerContributorInfo?.idUser);
      })
      .catch(() => {
        setItemLoading(key, false);
        setIsLoading(false);
      });
  };

  const handleRemoveCollaboratorSuccess = (): void => {
    if (designerContributorInfo.idUser && currentUserId === designerContributorInfo.idUser && typeof window.PMW.mystuff.onRemoveCollaboratorFromPoster === 'function') {
      window.PMW.mystuff?.onRemoveCollaboratorFromPoster(posterHashedId);
    }
  }

  const onClickRemove = (): void => {
    setItemLoading(ShareDesignAccessLoadingKeys.REMOVE, true);
    setIsLoading(true);
    void window.PMW.writeLocal('poster/removePosterContributor', {
      posterHashedId: posterHashedId,
      idUser: designerContributorInfo?.idUser,
    })
      .then(() => {
        setItemLoading(ShareDesignAccessLoadingKeys.REMOVE, false);
        setIsLoading(false);
        onRemoveUser(designerContributorInfo?.idUser);
        handleRemoveCollaboratorSuccess();
        closeModal();
      })
      .catch((response: unknown) => {
        setItemLoading(ShareDesignAccessLoadingKeys.REMOVE, false);
        setIsLoading(false);
        openErrorModal({message: getRemoveContributorErrorMessage(response)});
      });
  };

  const onClickResend = (): void => {
    if (isResendCooldownActive) {
      return;
    }
    setIsLoading(true);
    setIsResendCooldownActive(true);
    setTimeout(() => {
      setIsResendCooldownActive(false);
    }, 60000);
    void window.PMW.writeLocal('user/resendContributorInvitationForPoster', {
      emails: [designerContributorInfo.email],
      id: posterHashedId,
    })
      .then(() => {
        setIsLoading(false);
        onResendInvite(designerContributorInfo.email, InviteStateType.SUCCESS);
        closeModal();
      })
      .catch(() => {
        setIsLoading(false);
        onResendInvite(designerContributorInfo.email, InviteStateType.ERROR);
      });
  };

  const getCollaboratorOpts = (): ReactElement<any> | undefined => {
    if (designerContributorInfo?.isOwner) {
      return <Text val={window.i18next.t('pmwjs_owner')} size={TextSize.XXSMALL} className="content-sub-text spacing-p-r-8" />;
    }

    if (designerContributorInfo?.permissionLevel === DesignPermissionLevel.INVITE_SENT) {
      return <div className={isLoading ? 'spacing-m-r-2' : ''}>{getResendIcon()}</div>;
    }

    if (designerContributorInfo?.idUser === currentUserId && !designerContributorInfo?.isOwner) {
      return getRemoveButton();
    }

    if (idDesignOwner === currentUserId && !designerContributorInfo?.isOwner) {
      return getDropdown();
    }

    if (designerContributorInfo?.idUser !== currentUserId && !designerContributorInfo?.isOwner) {
      return (
        <Text val={getDesignPermissionLevelText(designerContributorInfo.permissionLevel)} size={TextSize.XXSMALL} className="content-sub-text spacing-p-r-6 capitalize-sentence" />
      );
    }

    return undefined;
  };

  const getRemoveButton = (): ReactElement<any> => {
    return (
      <Button
        icon="icon-delete"
        type={Type.SECONDARY}
        size={Size.XSMALL}
        isLoading={isLoading}
        onClick={onClickRemove}
        text={window.i18next.t('pmwjs_remove')}
        iconClassName="content-danger"
      />
    );
  };

  const getResendIcon = (): ReactElement<any> => {
    const icon = (
      <Icon
        icon="icon-rotate-ccw"
        type={IconType.SECONDARY}
        size={IconSize.SIZE_ICON_16}
        shape={IconShape.SQUARE}
        isLoading={isLoading}
        onClick={onClickResend}
        className="_unmargin-bottom"
        isDisabled={isResendCooldownActive}
      />
    );

    if (isResendCooldownActive) {
      return (
        <OverlayTrigger
          overlay={<Tooltip className="spacing-m-b-2 _rounded-arrow">{window.i18next.t('pmwjs_resend_invite_cooldown_tooltip')}</Tooltip>}
          placement="top"
          trigger={['hover', 'focus']}
        >
          <div>{icon}</div>
        </OverlayTrigger>
      );
    }

    return icon;
  };

  const closeModal = (): void => {
    if (modalData?.panelProps?.panelId) {
      dispatch(closePanel(modalData.panelProps.panelId as string));
    }
  };

  const getSelector = (): ReactElement<any> => {
    return (
      <DropdownDefaultSelectBox
        text={getDesignPermissionLevelText(designerContributorInfo?.permissionLevel)}
        textClassName="body-sm-regular flex-1 ct capitalize-sentence"
        className={`spacing-m-t-1 spacing-m-b-1 spacing-m-r-1 content-body ${isDropdownOpen ? 'border-s-darker' : '_border-s-transparent'}`}
        isLoading={isLoading}
      />
    );
  };

  const getDropdown = (): ReactElement<any> => {
    return (
      <Dropdown
        selector={getSelector()}
        popup={getPopup()}
        onOpen={() => {
          setIsDropdownOpen(true);
        }}
        onClose={() => {
          setIsDropdownOpen(false);
        }}
        popUpHasCustomWidth
        alignment={ALIGNMENT_TYPE.BOTTOM_START}
        position={DROPDOWN_POSITION.FIXED}
        popupClassName={styles.dropdownDesign}
        showModalForSmallScreen
        panelIdForModal={`share-options-dropdown-${designerContributorInfo?.idUser}`}
        modalTitle={window.i18next.t('pmwjs_change_permission')}
        popupOffset={8}
      />
    );
  };

  const getPopup = (): ReactElement<any> => {
    return (
      <ul className="_unpadded _unmargin" key={`contributor-opts-dropdown-${designerContributorInfo?.idUser}`}>
        <ControlledListItemType3
          id="edit"
          text={window.i18next.t('pmwjs_can_edit')}
          listSize={ControlledListItemSize.SMALL}
          onClick={() => {
            onClickPermissionLevel(DesignPermissionLevel.CAN_EDIT);
          }}
          isSelected={designerContributorInfo?.permissionLevel === DesignPermissionLevel.CAN_EDIT}
          textClass="capitalize-sentence"
          isLoading={loadingStates[ShareDesignAccessLoadingKeys.EDIT]}
        />
        <ControlledListItemType3
          id="view"
          text={window.i18next.t('pmwjs_can_view')}
          listSize={ControlledListItemSize.SMALL}
          onClick={() => {
            onClickPermissionLevel(DesignPermissionLevel.CAN_VIEW);
          }}
          isSelected={designerContributorInfo?.permissionLevel === DesignPermissionLevel.CAN_VIEW}
          textClass="capitalize-sentence"
          isLoading={loadingStates[ShareDesignAccessLoadingKeys.VIEW]}
        />
        <li>
          <Divider />
        </li>
        <ControlledListItemType7
          id="remove"
          text={window.i18next.t('pmwjs_remove')}
          listSize={ControlledListItemSize.SMALL}
          onClick={onClickRemove}
          icon="icon-delete"
          color={ColorTypes.danger}
          textClasses={`${styles.dropdownOptionRemove} content-danger`}
          isLoading={loadingStates[ShareDesignAccessLoadingKeys.REMOVE]}
        />
      </ul>
    );
  };

  return <> {getCollaboratorOpts()} </>;
}
